import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  Input,
  InputProps,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import FormLabelCustom from 'components/FormLabel';
import { forwardRef } from 'react';
import { Control, Controller, FieldValues, Path, useController } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import {
  AccountLateCharges,
  CDC_PENALTY_NOTICE,
  lateChargesSummary,
  MAX_INTEREST_PERCENT,
  MAX_PENALTY_PERCENT,
  PenaltyMode,
} from 'utils/lateCharges';

// O <Input> do Chakra já com o estilo dos outros campos, para o NumericFormat
// usar como `customInput`.
const FieldInput = forwardRef<HTMLInputElement, InputProps>((props, ref) => (
  <Input
    ref={ref}
    size="sm"
    fontSize="md"
    bg="white"
    {...props}
  />
));
FieldInput.displayName = 'LateChargesFieldInput';

type Props<T extends FieldValues> = {
  control: Control<T>;
  nameMode: Path<T>;
  namePenaltyPercent: Path<T>;
  namePenaltyAmount: Path<T>;
  nameInterest: Path<T>;
  placeholders: { percent: string; amount: string; interest: string };
  /** Rótulo do campo em R$, que diz qual é o teto (cobrança ou parcela). */
  amountLabel: string;
  /**
   * Padrão da conta. Sem ele o usuário não vê que a conta usa multa em R$ (a tela
   * abre em "Multa em %", cujo placeholder fica neutro). Fora do card das
   * Configurações, que já mostra o "Vigente".
   */
  accountDefault?: AccountLateCharges | null;
};

/**
 * Multa (em % ou R$) e juros ao mês. Validação fica no schema/handler de cada
 * tela (`lateChargesFieldErrors`), porque o teto da multa fixa depende do valor
 * da cobrança ou da parcela.
 */
export default function LateChargesFields<T extends FieldValues>({
  control,
  nameMode,
  namePenaltyPercent,
  namePenaltyAmount,
  nameInterest,
  placeholders,
  amountLabel,
  accountDefault,
}: Props<T>) {
  const { field: modeField } = useController({ control, name: nameMode });
  const mode: PenaltyMode = modeField.value === 'AMOUNT' ? 'AMOUNT' : 'PERCENT';

  return (
    <>
      {accountDefault && (
        <Text
          fontSize="sm"
          color="gray.600"
          mb={2}
        >
          Padrão da conta: {lateChargesSummary(accountDefault)}
        </Text>
      )}
      <ButtonGroup
        size="sm"
        isAttached
        mb={3}
      >
        <Button
          variant={mode === 'PERCENT' ? 'solid' : 'outline'}
          colorScheme="brand"
          onClick={() => modeField.onChange('PERCENT')}
        >
          Multa em %
        </Button>
        <Button
          variant={mode === 'AMOUNT' ? 'solid' : 'outline'}
          colorScheme="brand"
          onClick={() => modeField.onChange('AMOUNT')}
        >
          Multa em R$
        </Button>
      </ButtonGroup>
      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        spacing={4}
      >
        {mode === 'AMOUNT' ? (
          <Controller
            control={control}
            name={namePenaltyAmount}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormControl isInvalid={!!error}>
                <FormLabelCustom
                  label={amountLabel}
                  name={namePenaltyAmount}
                  props={{}}
                />
                <NumericFormat
                  id={namePenaltyAmount}
                  name={namePenaltyAmount}
                  // O NumericFormat tipa `customInput` pelos atributos nativos do
                  // <input>, onde `size` é número; no Chakra é 'sm' | 'md'.
                  customInput={FieldInput as any}
                  inputMode="decimal"
                  prefix="R$ "
                  decimalScale={2}
                  fixedDecimalScale
                  decimalSeparator=","
                  thousandSeparator="."
                  allowNegative={false}
                  placeholder={placeholders.amount}
                  value={(value as number | undefined) ?? ''}
                  // Vazio vira undefined: usa o padrão da conta.
                  onValueChange={(values) => onChange(values.floatValue)}
                  onBlur={onBlur}
                />
                <FormErrorMessage>{error?.message}</FormErrorMessage>
              </FormControl>
            )}
          />
        ) : (
          <Controller
            control={control}
            name={namePenaltyPercent}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormControl isInvalid={!!error}>
                <FormLabelCustom
                  label={`Multa (%) — até ${MAX_PENALTY_PERCENT}%`}
                  name={namePenaltyPercent}
                  props={{}}
                />
                <FieldInput
                  id={namePenaltyPercent}
                  name={namePenaltyPercent}
                  inputMode="decimal"
                  placeholder={placeholders.percent}
                  value={(value as string | undefined) ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                />
                <FormErrorMessage>{error?.message}</FormErrorMessage>
              </FormControl>
            )}
          />
        )}
        <Controller
          control={control}
          name={nameInterest}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <FormControl isInvalid={!!error}>
              <FormLabelCustom
                label={`Juros ao mês (%) — até ${MAX_INTEREST_PERCENT}%`}
                name={nameInterest}
                props={{}}
              />
              <FieldInput
                id={nameInterest}
                name={nameInterest}
                inputMode="decimal"
                placeholder={placeholders.interest}
                value={(value as string | undefined) ?? ''}
                onChange={onChange}
                onBlur={onBlur}
              />
              <FormErrorMessage>{error?.message}</FormErrorMessage>
            </FormControl>
          )}
        />
      </SimpleGrid>
      <Text
        fontSize="xs"
        color="gray.500"
        mt={2}
      >
        {CDC_PENALTY_NOTICE}
      </Text>
    </>
  );
}
